/**
 * `meta.profile` stamping helper.
 *
 * Derives the expected canonical profile from the resource (server-derived;
 * never trusts client-supplied `meta.profile`) and REPLACES any existing
 * Unify-owned profile URL with the single expected canonical, while preserving
 * any non-Unify profiles (e.g. US Core) the resource legitimately carries.
 */
import type { Resource } from '@medplum/fhirtypes';

import { resolveExpectedProfile } from './profile-resolver';
import { UNIFY_PROFILE_PREFIX } from './profile-urls';

/**
 * Returns a copy of `resource` with the correct Unify `meta.profile` stamped.
 * For unprofiled resource types the resource is returned unchanged.
 */
export function withUnifyProfile<T extends Resource>(resource: T): T {
  const expected = resolveExpectedProfile(resource);
  if (!expected) return resource;

  const existing = resource.meta?.profile ?? [];
  const preserved = existing.filter((p) => !p.startsWith(UNIFY_PROFILE_PREFIX));
  const profile = [...preserved, expected];

  return { ...resource, meta: { ...resource.meta, profile } };
}

/**
 * Returns the missing-profile issue (if any) for a resource that has already
 * been (or should have been) stamped. Used by dry-run / preflight to fail
 * writes that lack their expected profile.
 */
export function hasExpectedProfile(resource: Resource): boolean {
  const expected = resolveExpectedProfile(resource);
  if (!expected) return true; // unprofiled types are always "fine"
  return (resource.meta?.profile ?? []).includes(expected);
}
